import LogoWithContainer from 'assets/LogoWithContainer'
import styles from './PageHeader.module.scss'
import LanguageSwitcher from 'components/language-switcher/LanguageSwitcher'
import NavigationDesktop from 'components/page-header/navigation-desktop/NavigationDesktop'
import { Button } from '@boltenergy-be/design-system'
import NavigationMobile from 'components/page-header/navigation-mobile/NavigationMobile'
import { useRef, useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { default as NextLink } from 'next/link'
import router from 'next/router'
import { routes } from 'utils/routes'
import { PageHeaderProps } from 'components/page-header/types'
import { store } from 'store'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import Link from 'components/link/Link'
import { PageFlag } from 'components/page-flag/PageFlag'
import { getPortalUrl, trackEvent, trackGTMEvent } from 'utils/tracking'
import { CommonGTMEvents } from 'types/tracking'
import { useStoreSelector } from 'hooks/store'
import classNames from 'classnames'
import { PLENTY_URL } from 'constants/constants'
import { useGetLanguage } from 'hooks/useGetLanguage'

/**
 * The fixed site header: page flag, Bolt/Plenty and language switch, navigation and the simulation CTA.
 *
 * @figma Navigation (Web Sections) https://www.figma.com/design/bb4ffCw3RlErklnLuAM4DN/Web-Sections?node-id=7-10717 — Theme→highContrast (High contrast→true, Dark/Light→false), Brand: n/a (Bolt only), Breakpoint: n/a, Language: n/a (from the locale)
 */
const PageHeader = ({ localized, extraStartSimulationAction, highContrast, simulationSalesOffice }: PageHeaderProps) => {
  // Redux
  const { didSimulation } = useStoreSelector((store) => store.customer.flows.simulation)

  // i18n
  const { t } = useTranslation(['header', 'common'])
  const language = useGetLanguage()

  // Refs
  const menuRef = useRef<HTMLDialogElement>(null)

  // Local state
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * Handles the banner on click event
   * Shows simulation or redirects to producer page if simulation has been done
   */
  const handleOnBannerClick = () => {
    if (didSimulation) {
      return router.push(routes(language).producers)
    } else {
      // Track 'Simulation Started' event
      trackGTMEvent(CommonGTMEvents.SIMULATION_STARTED)
      trackEvent({ category: 'simulation', action: 'start' })

      return router.push(routes(language).simulation)
    }
  }

  /**
   * Handles the simulation button click event
   */
  const onSimulationClick = async () => {
    setLoading(true)

    try {
      if (router.pathname.includes('move')) {
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: 'Move Page' }))
      }

      await extraStartSimulationAction?.()

      void startSimulationOrRegistration({ simulationSalesOffice })
    } finally {
      setLoading(false)
    }
  }

  return (
    <header className={styles.header}>
      <PageFlag onClick={handleOnBannerClick} />

      <section className={styles['small-bar']}>
        <ul className={styles.environments}>
          <li className={styles.active}>
            <strong>Bolt</strong>
          </li>
          <li>
            <Link href={`${PLENTY_URL}/${language}?simulation_so=Bolt%20Website`} as="a" target="_blank">
              Plenty
            </Link>
          </li>
        </ul>

        <LanguageSwitcher {...{ localized }} />
      </section>

      <section className={classNames(styles['main-navigation'], { [styles['high-contrast']]: highContrast })}>
        <NextLink className={styles.logo} href={routes(language).home}>
          <LogoWithContainer />
        </NextLink>

        <NavigationDesktop />

        <div className={styles.cta}>
          <Link representation="button" className={styles['my-bolt']} variant="layered" href={getPortalUrl()} suppressHydrationWarning>
            {t(($) => $.buttons.myBolt)}
          </Link>

          <Button suppressHydrationWarning variant="primary" onClick={onSimulationClick} {...{ loading }}>
            {t(($) => $.calculateProposal, { ns: 'common' })}
          </Button>
        </div>

        <Button
          leadingIcon="menu"
          className={styles.hamburger}
          variant="tertiary"
          type="button"
          onClick={() => menuRef?.current?.showModal()}
        />

        <NavigationMobile {...{ onSimulationClick, loading }} ref={menuRef} />
      </section>
    </header>
  )
}

export default PageHeader
